import { motion } from "framer-motion";
import HeroProductShot from "./HeroProductShot";
import { FolderLock, Fingerprint, ShieldCheck, WifiOff } from "lucide-react";

const HEADLINE = ["Planen.", "Dokumentieren.", "Auswerten."];

const usps = [
  { icon: FolderLock, label: "Digitale Fallakte" },
  { icon: Fingerprint, label: "Muster erkennen" },
  { icon: ShieldCheck, label: "Datenschutz im Fokus" },
  { icon: WifiOff, label: "Offline First" },
];

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-32 md:pt-40 pb-24 md:pb-32 overflow-hidden"
    >
      {/* Grid background */}
      <div className="absolute inset-0 bp-grid opacity-70" aria-hidden />

      {/* Ambient orbs */}
      <div
        aria-hidden
        className="absolute -left-40 top-20 w-[520px] h-[520px] rounded-full"
        style={{
          background:
            "radial-gradient(circle, rgba(0,250,248,0.10), transparent 60%)",
          filter: "blur(40px)",
        }}
      />

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-8 items-center">
        {/* Left: headline + subtitle + CTA */}
        <div className="lg:col-span-6 xl:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.7 }}
            className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-3.5 py-1.5 mb-8"
          >
            <span className="cyan-dot" />
            <span className="text-xs font-mono-bp uppercase tracking-[0.25em] text-white/70">
              BeParent · Version 1.0
            </span>
          </motion.div>

          <h1
            data-testid="hero-headline"
            className="font-display font-semibold text-white text-[52px] sm:text-[72px] md:text-[92px] lg:text-[104px] leading-[0.92] tracking-[-0.04em]"
          >
            <span className="sr-only">
              BeParent – Dokumentations-App für getrennte Eltern:
            </span>
            {HEADLINE.map((word, i) => (
              <span key={word} className="line-mask block">
                <motion.span
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{
                    duration: 0.95,
                    ease: [0.22, 1, 0.36, 1],
                    delay: 0.35 + i * 0.12,
                  }}
                  className="block"
                >
                  {i === HEADLINE.length - 1 ? (
                    <>
                      <span className="text-white">{word.slice(0, -1)}</span>
                      <span className="text-[#00FAF8]">.</span>
                      <span className="bp-caret" aria-hidden />
                    </>
                  ) : (
                    word
                  )}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.9 }}
            className="mt-8 text-lg md:text-xl max-w-xl text-white/60 leading-relaxed"
          >
            Strukturierte Dokumentation für Eltern in familienrechtlichen
            Verfahren. Eine ruhige, verschlüsselte digitale Fallakte —
            entwickelt für Klarheit, nicht für Emotion.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.9 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href="#produkt"
              data-testid="hero-cta-secondary"
              className="group inline-flex items-center gap-2 rounded-full border border-white/10 px-6 py-4 text-[15px] text-white/85 hover:border-white/25 hover:text-white transition-colors"
            >
              <span className="font-mono-bp text-[11px] uppercase tracking-[0.2em] text-[#00FAF8]">
                01
              </span>
              Was ist BeParent
            </a>
          </motion.div>

          {/* USP strip */}
          <motion.ul
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2, duration: 1 }}
            data-testid="hero-usps"
            className="mt-14 grid grid-cols-2 sm:grid-cols-4 gap-y-4 gap-x-6 max-w-xl"
          >
            {usps.map(({ icon: Icon, label }) => (
              <li
                key={label}
                className="flex items-center gap-2.5 text-[13px] text-white/70"
              >
                <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-[#00FAF8]/10 border border-[#00FAF8]/25">
                  <Icon size={14} className="text-[#00FAF8]" />
                </span>
                {label}
              </li>
            ))}
          </motion.ul>
        </div>

        {/* Right: real product shot */}
        <div className="lg:col-span-6 xl:col-span-5 flex justify-center lg:justify-end">
          <HeroProductShot />
        </div>
      </div>

      {/* Scroll hint */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="hidden md:flex absolute bottom-8 left-1/2 -translate-x-1/2 flex-col items-center gap-2 text-white/40"
        aria-hidden
      >
        <span className="text-[10px] font-mono-bp uppercase tracking-[0.3em]">
          Scrollen
        </span>
        <span className="h-8 w-px bg-gradient-to-b from-white/40 to-transparent" />
      </motion.div>
    </section>
  );
}
